import React, {useCallback, useEffect, useMemo, useState} from "react";
import {MyBreadcrumb, MyButton, MyText} from "../../../../../components";
import {
  Col,
  Row,
  Input,
  Checkbox,
  Form,
  Button,
  Spin,
  message,
  TabPaneProps,
  Card,
  Flex,
  Select,
  Popconfirm
} from "antd";
import {DeleteOutlined, LeftOutlined, MoreOutlined, PlusOutlined, RightOutlined} from "@ant-design/icons";
import {addQuestionAnswerAction, getQuestionAnswerAction} from "../../../../../store/pages/plans/actions";
import {useDispatch, useSelector} from "react-redux";
import {useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {selectStore} from "../../../../../store/selector";
import {RootState} from "../../../../../store";
import {useTranslation} from "react-i18next";
import {v4 as uuidv4} from 'uuid';
import {TextEditor} from "../../../../../components/common/TextEditor";
import {Color} from "../../../../../themes";

const FORM_ITEM_NAME = {
  ID: 'id',
  TEST_ID: 'test_id',
  NAME: 'name',
  DIFFICULTY: 'difficulty',
  ANSWERS: 'answers',
  QUESTIONS: 'questions',
  IS_CORRECT: 'is_correct',
}

export const Test = () => {
  const {t} = useTranslation("common")

  const [testForm] = Form.useForm()
  const dispatch = useDispatch()
  const {testId} = useParams()
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate()

  const difficult = [ t('common:easy'),  t('common:average'),  t('common:hard')]

  useEffect(() => {
    dispatch(getQuestionAnswerAction(testId))
  }, [])

  let itemInitialState = {
    [FORM_ITEM_NAME.ID]: null,
    [FORM_ITEM_NAME.NAME]: t('question'),
    [FORM_ITEM_NAME.DIFFICULTY]: null,
    [FORM_ITEM_NAME.ANSWERS]: [{
      [FORM_ITEM_NAME.ID]: null,
      [FORM_ITEM_NAME.NAME]: null,
      [FORM_ITEM_NAME.IS_CORRECT]: false
    }]
  }

  let arrayInitialState = {
    [FORM_ITEM_NAME.TEST_ID]: null,
    [FORM_ITEM_NAME.QUESTIONS]: []
  }

  const {plans: {oneTest: testState}}: RootState = useSelector(selectStore)

  const [activeKey, setActiveKey] = useState<string>('');
  const [isEditMode, setIsEditMode] = useState<boolean>(true)

  console.log('testForm', {
    test: testForm.getFieldsValue(),
    key: activeKey
  })

  useEffect(() => {
    if (testState?.success) {
      let arr = testState.data?.[FORM_ITEM_NAME.QUESTIONS]?.length > 0 ? testState.data?.[FORM_ITEM_NAME.QUESTIONS] : [itemInitialState]

      setActiveKey(arr[0]?.key)

      let body = {
        [FORM_ITEM_NAME.TEST_ID]: testId,
        [FORM_ITEM_NAME.QUESTIONS]: arr
      }

      testForm.setFieldsValue(body)
    }
  }, [testState?.success])

  const onChangeTab = (key: string) => {
    if(key){
      setActiveKey(key);
      setIsEditMode(true)
    }
  }

  const addQuestion = () => {
    let key = uuidv4()

    testForm.setFieldsValue({
      [FORM_ITEM_NAME.TEST_ID]: testId,
      [FORM_ITEM_NAME.QUESTIONS]: [...testForm.getFieldsValue()?.[FORM_ITEM_NAME.QUESTIONS], {
        ...itemInitialState,
        key: key
      }]
    })

    setActiveKey(key);
    setIsEditMode(true)
  }

  const removeQuestion = (key: string) => {
    testForm.setFieldsValue({
      [FORM_ITEM_NAME.TEST_ID]: testId,
      [FORM_ITEM_NAME.QUESTIONS]: testForm.getFieldsValue()?.[FORM_ITEM_NAME.QUESTIONS]?.filter((item: any) => item.key !== key)
    })

    message.warning(t('dontForgetToSaveTest'))

    setActiveKey(testForm.getFieldsValue()?.[FORM_ITEM_NAME.QUESTIONS]?.[0]?.key)

    setIsEditMode(true)
  }

  const onFinish = (values: any) => {
    dispatch(addQuestionAnswerAction(values))
  }

  return (
    <>
      <MyBreadcrumb title={t('back')} isBack extra={(
        <MyButton type='primary' ghost
                  onClick={() => setIsEditMode(prevState => !prevState)}>{isEditMode ? t('preview') : t('edit')}</MyButton>
      )}/>

      <Row justify='center'>
        <Col span={20}>
          <Row gutter={[16, 16]}>
            <Col span={18}>
              <Card title={(
                <Row align='middle' justify='space-between'>
                  <Col flex={'200px'}>
                    {
                      testForm.getFieldsValue()?.[FORM_ITEM_NAME.QUESTIONS]?.[0]?.key !== activeKey &&
                      <Flex align='center' justify='start' gap={8} style={{cursor: 'pointer'}}
                            onClick={() => onChangeTab(testForm.getFieldsValue()?.[FORM_ITEM_NAME.QUESTIONS][testForm.getFieldsValue()?.[FORM_ITEM_NAME.QUESTIONS].findIndex((item: any) => item.key === activeKey) - 1].key)}
                      >
                        <LeftOutlined/>
                        <MyText>
                          {t('prev')}
                        </MyText>
                      </Flex>
                    }

                  </Col>

                  <Col>
                    <MyText>
                      {t('question')}
                      №{testForm.getFieldsValue()?.[FORM_ITEM_NAME.QUESTIONS]?.findIndex((item: any) => item.key === activeKey) + 1}
                    </MyText>
                  </Col>

                  <Col flex={'200px'}>
                    {
                      testForm.getFieldsValue()?.[FORM_ITEM_NAME.QUESTIONS]?.[testForm.getFieldsValue()?.[FORM_ITEM_NAME.QUESTIONS].length - 1]?.key === activeKey
                        ?
                        <Flex align='center' justify='end' gap={8} style={{cursor: 'pointer'}} onClick={addQuestion}>
                          <MyText>
                            {t('addQuestion')}
                          </MyText>
                          <PlusOutlined/>
                        </Flex>
                        :
                        <Flex align='center' justify='end' gap={8} style={{cursor: 'pointer'}}
                              onClick={() => onChangeTab(testForm.getFieldsValue()?.[FORM_ITEM_NAME.QUESTIONS]?.[testForm.getFieldsValue()?.[FORM_ITEM_NAME.QUESTIONS]?.findIndex((item: any) => item.key === activeKey) + 1]?.key)}>
                          <MyText>
                            {t('next')}
                          </MyText>
                          <RightOutlined/>
                        </Flex>
                    }

                  </Col>
                </Row>
              )} loading={testState.loading}>
                <Form
                  autoComplete="off"
                  form={testForm}
                  name="form"
                  onFinish={onFinish}
                  initialValues={arrayInitialState}
                  disabled={!isEditMode}
                >
                  <Row gutter={[0, 16]}>
                    <Col span={24} style={{display: 'none'}}>
                      <Form.Item name={FORM_ITEM_NAME.TEST_ID} hidden>
                        <Input hidden/>
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.List name={FORM_ITEM_NAME.QUESTIONS}>
                        {
                          (fields, operation, meta) => {
                            return (
                              fields.map((item, index) => {
                                let currentItem: any = testForm.getFieldsValue()?.[FORM_ITEM_NAME.QUESTIONS]?.[item.key]

                                return currentItem?.key === activeKey && (
                                  <Form.Item key={item.key}>
                                    <Row gutter={[16, 16]}>
                                      <Col span={24} style={{display: 'none'}}>
                                        <Form.Item name={[item.name, FORM_ITEM_NAME.ID]} hidden>
                                          <Input hidden/>
                                        </Form.Item>
                                      </Col>
                                      <Col span={24}>

                                        <Form.Item labelCol={{span: 24}} wrapperCol={{span: 24}}
                                                   name={[item.name, FORM_ITEM_NAME.DIFFICULTY]}
                                                   label={isEditMode ? t('questionLevel') : ''}>
                                          {
                                            isEditMode ?
                                              <Select
                                                options={difficult.map((dif, difIndex) => ({
                                                  key: difIndex + 1,
                                                  label: dif,
                                                  value: difIndex + 1
                                                }))}/>
                                              :
                                              <MyText>
                                                {t('questionLevel')}: {difficult[currentItem?.difficulty - 1]}
                                              </MyText>
                                          }

                                        </Form.Item>
                                      </Col>
                                      <Col span={24}>
                                        <Form.Item labelCol={{span: 24}} wrapperCol={{span: 24}}
                                                   name={[item.name, FORM_ITEM_NAME.NAME]}
                                                   label={isEditMode ? t('enterQuestion') : ''}>
                                          <TextEditor
                                            value={currentItem?.[FORM_ITEM_NAME.NAME]}
                                            disabled={!isEditMode}
                                          />
                                        </Form.Item>
                                      </Col>
                                      <Col span={24}>
                                        <Form.Item labelCol={{span: 24}} wrapperCol={{span: 24}}
                                                   label={t('answers')}
                                                   name={[item.name, FORM_ITEM_NAME.ANSWERS]}>
                                          <Form.List name={[item.name, FORM_ITEM_NAME.ANSWERS]}>
                                            {
                                              (subFields, subOpt) => {
                                                return (
                                                  <Row gutter={[16, 16]}>
                                                    {subFields.map((subField) => {
                                                      let currentInnerItem = currentItem?.[FORM_ITEM_NAME.ANSWERS]?.[subField.name]

                                                      return (
                                                        <Col key={subField.key} span={24}>
                                                          <Row justify='space-between' align='middle'>
                                                            <Col span={24}>
                                                              <Card
                                                                style={{backgroundColor: isEditMode ? 'var(--c-light-hover)' : 'transparent'}}
                                                                styles={{
                                                                  body: {
                                                                    padding: 4
                                                                  },
                                                                  header: {
                                                                    padding: 4,
                                                                    minHeight: 'auto'
                                                                  }
                                                                }}
                                                                title={(
                                                                  <Row align='middle' justify='space-between'>
                                                                    <Col>
                                                                      <Form.Item noStyle
                                                                                 name={[subField.name, FORM_ITEM_NAME.IS_CORRECT]}
                                                                                 valuePropName="checked"
                                                                                 initialValue={false}
                                                                                 key={subField.key + 'checkbox'}
                                                                                 required={true}>
                                                                        <Checkbox>
                                                                          {t('answer')}
                                                                        </Checkbox>
                                                                      </Form.Item>
                                                                    </Col>

                                                                    <Col>
                                                                      {
                                                                        isEditMode && (
                                                                          <Button type='text' icon={<DeleteOutlined/>}
                                                                                  onClick={() => {
                                                                                    subOpt.remove(subField.name);
                                                                                  }}/>
                                                                        )
                                                                      }
                                                                    </Col>
                                                                  </Row>
                                                                )}>

                                                                <Row align='middle' gutter={[8, 8]}>
                                                                  <Col flex={1}>
                                                                    <Form.Item noStyle
                                                                               name={[subField.name, FORM_ITEM_NAME.NAME]}
                                                                               required={true}
                                                                               key={subField.key + 'input'}>
                                                                      <TextEditor
                                                                        value={currentInnerItem?.[FORM_ITEM_NAME.NAME]}
                                                                        disabled={!isEditMode}
                                                                      />
                                                                    </Form.Item>
                                                                  </Col>
                                                                </Row>
                                                              </Card>
                                                            </Col>
                                                          </Row>
                                                        </Col>
                                                      )
                                                    })}
                                                    {
                                                      isEditMode &&
                                                      (
                                                        subFields.length <= 4 &&
                                                        <Col span={8}>
                                                          <Button type="primary" onClick={() => {
                                                            subOpt.add()
                                                          }} block>
                                                            {t('addAnswerVariant')}
                                                          </Button>
                                                        </Col>
                                                      )
                                                    }
                                                  </Row>
                                                )
                                              }
                                            }
                                          </Form.List>
                                        </Form.Item>
                                      </Col>
                                    </Row>
                                  </Form.Item>
                                )
                              })
                            )
                          }
                        }
                      </Form.List>
                    </Col>
                    {
                      isEditMode &&
                      <Col span={24}>
                        <Row justify='center' gutter={[16, 16]}>
                          <Col>
                            <Popconfirm
                              title={t('deleteQuestion')}
                              description={t('areYouAgreeToDeleteQuestion')}
                              onConfirm={() => removeQuestion(activeKey)}
                              okText={t('yes')}
                              cancelText={t('no')}
                            >
                              <MyButton danger type='primary'>
                                {t('deleteQuestion')}
                              </MyButton>
                            </Popconfirm>
                          </Col>
                          <Col>
                            <Form.Item>
                              <MyButton type='primary' ghost htmlType='submit'>{t('save')}</MyButton>
                            </Form.Item>
                          </Col>
                        </Row>
                      </Col>
                    }
                  </Row>
                </Form>
              </Card>
            </Col>

            <Col span={6}>
              <Card loading={testState.loading}>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <MyText color={Color.titleText} weight={500} size={16}>
                      {t('totalQuestions', {count: testForm.getFieldsValue()?.[FORM_ITEM_NAME.QUESTIONS]?.length})}
                    </MyText>
                  </Col>

                  <Col span={24}>
                    <Flex wrap='wrap' gap={4}>
                      {
                        testForm?.getFieldsValue()?.[FORM_ITEM_NAME.QUESTIONS]?.map((item: any, index: any) => (
                          <Flex style={{
                            minWidth: 40,
                            minHeight: 40,
                            width: 40,
                            height: 40,
                            border: `1px solid ${activeKey === item?.key ? Color.hover : Color.border}`,
                            borderRadius: 8,
                            cursor: 'pointer',
                            background: activeKey === item?.key ? Color.hover : 'transparent'
                          }} key={item?.key} align='center' justify='center' onClick={() => onChangeTab(item?.key)}>
                            <MyText color={activeKey === item?.key ? '#fff' : Color.subtitle}>
                              {index + 1}
                            </MyText>
                          </Flex>
                        ))
                      }
                    </Flex>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  )
}